/**
 * Copyright 2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

/**
 * Ported from @material-ui/core@4.11.3
 */

import * as React from 'react';
import cx from 'classnames';
import styles from './styles.module.scss';

type Props = {
  /**
   * Absolutely position the element.
   */
  absolute?: boolean;

  /**
   * If `true`, a vertical divider will have the correct height when used in flex container.
   * (By default, a vertical divider will have a calculated height of `0px` if it is the child of a flex container.)
   */
  flexItem?: boolean;

  /**
   * If `true`, the divider will have a lighter color.
   */
  light?: boolean;

  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   */
  component?: React.ElementType;

  /**
   * The divider orientation.
   */
  orientation?: 'horizontal' | 'vertical';

  /**
   * The variant to use.
   */
  variant?: 'fullWidth' | 'inset' | 'middle';

  /**
   * @ignore
   */
  className?: string;
  role?: string;
  ref?: React.RefObject<HTMLElement>;
};

export function Divider({ ref, ...props }: Props) {
  const {
    absolute = false,
    className,
    component: Component = 'hr',
    flexItem = false,
    light = false,
    orientation = 'horizontal',
    role = Component !== 'hr' ? 'separator' : undefined,
    variant = 'fullWidth',
    ...other
  } = props;

  return (
    <Component
      className={cx(
        styles.root,
        {
          [styles[variant]]: variant !== 'fullWidth',
          [styles.absolute]: absolute,
          [styles.flexItem]: flexItem,
          [styles.light]: light,
          [styles.vertical]: orientation === 'vertical',
        },
        className,
      )}
      role={role}
      ref={ref}
      {...other}
    />
  );
}
